import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import { AuthData } from '../../../context/auth';
import {
	EndorsementData,
	withEndorsementContext,
} from '../../../context/EndorsementProvider';
import { ExposureCreateRequest, Peril } from '../../../types/policy';
import {
	StyledSectionContainer,
	StyledTitle,
	StyledTitleContainer,
} from '../../common';
import { errorToast, successToast } from '../../Toast';
import ConfirmationModal from '../ConfirmationModal';
import AddPerilModal from './AddEndorsementPerilModal';
import PerilsList from './PerilsList';

interface Props {
	authData: AuthData;
	endorsementData: EndorsementData;
}

/**
 * The peril list for an added exposure on an endorsement.
 * This list can only contain added perils.
 */
const AddedExposurePerils = (props: Props) => {
	const { t } = useTranslation();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;
	const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
	const [removedPeril, setRemovePeril] = React.useState(null);
	const params = useParams();
	const { exposureId } = params;
	const addedExposureIndex = parseInt(exposureId.split('-')[1], 10);

	const { endorsementData, authData } = props;

	const { productConfig, endorsementUpdateObj, saveFastForm, setLoading } =
		endorsementData;
	const selectedExposure =
		endorsementData.selectedExposure as ExposureCreateRequest;
	const isInEditMode =
		authData.hasPermission('endorsement.update') &&
		endorsementUpdateObj.state === 'application';

	if (!selectedExposure) {
		return null;
	}

	const removePeril = async () => {
		setIsConfirmModalOpen(false);
		setLoading(true);
		try {
			await saveFastForm(getValues(), dirtyFields, reset, {
				removeAddedPeril: {
					perilLocator: removedPeril.locator,
					exposureLocator: selectedExposure.locator,
					exposureIsAdded: true,
					exposureIndex: addedExposureIndex,
				},
			});
			successToast('Successfully removed peril');
		} catch (error) {
			errorToast('Error removing peril');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const policyConfig = productConfig.policyConfiguration;
	const exposureConfig = policyConfig.exposures.find(
		(e) => e.name === selectedExposure.exposureName,
	);
	const perilConfigs = exposureConfig.perils;

	const renderActions = () => {
		return (
			<AddPerilModal
				perilConfigs={perilConfigs}
				exposureLocator={selectedExposure.exposureLocator}
			/>
		);
	};

	const renderRemovePerilModal = () => {
		return (
			<ConfirmationModal
				entityName={removedPeril.name}
				entityType={t('peril.singular.lower')}
				authToken={authData.auth.authorizationToken}
				onRemove={removePeril}
				cancel={() => setIsConfirmModalOpen(false)}
			/>
		);
	};

	const openConfirmationModal = (peril: Peril) => {
		setIsConfirmModalOpen(true);
		setRemovePeril(peril);
	};

	return (
		<>
			{isConfirmModalOpen && renderRemovePerilModal()}
			<StyledTitleContainer>
				<StyledTitle>{t('peril.plural.upper')}</StyledTitle>
			</StyledTitleContainer>
			<hr />
			<StyledSectionContainer>
				<PerilsList
					perils={selectedExposure.perils}
					openConfirmationModal={(peril: Peril) => openConfirmationModal(peril)}
				/>
			</StyledSectionContainer>
			{isInEditMode && renderActions()}
		</>
	);
};

export default withEndorsementContext<Props>(AddedExposurePerils);
